import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { ProblemAddressResponse } from '@/core/generated/api';
import { problemAddressService } from '@/entities/ProblemAddress/api/problemAddressService';
import {
    addressPointCoordinatesToRequest,
    addressPointResponseToFormValues,
    getHouseCoordinates,
    parseCoordinatePair,
} from '@/entities/ProblemAddress/mappers';

import {
    addressPointFormSchema,
    houseCoordinatesSchema,
    type AddressPointFormValues,
    type VerifiedAddressPointValues,
} from '@/entities/ProblemAddress/model/schema';
import type { HouseCoordinates } from '@/entities/ProblemAddress/model/types';
import { getErrorMessage } from '@/shared/lib/errors';
import { formatCoordinateInput } from '@/shared/lib/coordinateInput';
import { isBadRequestError } from '@/core/api/badRequestError';

/** Управляет ручной проверкой координат дома */
export const useVerifyAddressPoint = (
    point: ProblemAddressResponse,
    onSaved: (point: ProblemAddressResponse, openNext: boolean) => Promise<void>,
) => {
    const form = useForm<AddressPointFormValues, unknown, VerifiedAddressPointValues>({
        resolver: zodResolver(addressPointFormSchema),
        defaultValues: addressPointResponseToFormValues(point),
    });

    const { reset, setValue, setError, clearErrors, handleSubmit, control } = form;
    const values = useWatch({ control });
    const validation = houseCoordinatesSchema.safeParse(values);

    const proposed = validation.success
        ? validation.data
        : null;

    const current = getHouseCoordinates(point);

    const coordinatesChanged = proposed
        ? !current || proposed.latitude !== current.latitude || proposed.longitude !== current.longitude
        : Boolean(form.formState.dirtyFields.latitude || form.formState.dirtyFields.longitude);

    useEffect(() => reset(addressPointResponseToFormValues(point)), [point, reset]);

    /** Выбирает координаты дома */
    const selectPoint = (coordinates: HouseCoordinates) => {
        setValue('latitude', formatCoordinateInput(coordinates.latitude, 2), {
            shouldDirty: true,
            shouldValidate: true
        });

        setValue('longitude', formatCoordinateInput(coordinates.longitude, 3), {
            shouldDirty: true,
            shouldValidate: true
        });

        clearErrors();
    };

    /** Подставляет координаты найденного дома */
    const applyPair = () => {
        const coordinates = parseCoordinatePair(form.getValues('coordinatePair'));

        if (!coordinates) {
            setError('coordinatePair', { message: 'Вставьте широту и долготу, например: 55.755864, 37.617698' });

            return;
        }

        selectPoint(coordinates);
    };

    /** Сохраняет результат проверки дома */
    const submit = async (coordinates: HouseCoordinates, openNext: boolean, confirm = false) => {
        if (!confirm && !coordinatesChanged) return;

        if (!point.id || !point.version) {
            setError('root.server', { message: 'Обновите карточку адреса перед сохранением' });

            return;
        }

        clearErrors('root.server');

        try {
            const updated = await problemAddressService.verify(point.id, addressPointCoordinatesToRequest(coordinates, point.version));

            toast.success('Координаты дома проверены');
            await onSaved(updated, openNext);
        } catch (failure: unknown) {
            if (!isBadRequestError(failure)) {
                setError('root.server', { message: getErrorMessage(failure, 'Не удалось сохранить координаты') });
            }
        }
    };

    /** Подтверждает текущую точку дома */
    const confirmCurrent = async () => {
        const current = getHouseCoordinates(point);

        if (!current || form.formState.isSubmitting) return;

        reset(addressPointResponseToFormValues(point));
        await handleSubmit(() => submit(current, false, true))();
    };

    return {
        form,
        proposed,
        coordinatesChanged,
        selectPoint,
        applyPair,
        confirmCurrent,
        save: handleSubmit((coordinates) => submit(coordinates, false)),
        saveAndNext: handleSubmit((coordinates) => submit(coordinates, true)),
    };
};
